import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik } from "formik";
import { Checkbox, Field, FormButtonCluster } from "../UI/forms";
import * as r from "../../requests/customerManagementRequests";
import AlertContext from "../../context/alert/AlertContext";
import { Flex, Box } from "reflexbox";
import { deleteAddress } from "../../requests/addressManagementRequests";
import styled, { useTheme } from "styled-components";
import { getCustomer, getCPQProfileOptions } from "../../requests/customerManagementRequests";
import { getPlatforms } from "../../requests/platformManagementRequests";
import { useQuery } from "../Hooks";
import AddressForm from "./AddressForm";
import { Body, Heading } from "../UI/Text";
import Button from "../UI/Button";
import Modal from "../UI/Modal";
import Card from "../UI/Card";
import { PageWrapper } from "../UI/Grid";
import Table from "../UI/Table";
import states from "../../modules/states";
import UserContext from "../../context/user/UserContext";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const validationSchema = yup.object().shape({
  pid: yup
    .string("Please select a platform to assign this customer to.")
    .required("Platform is required!"),
  companyName: yup.string().nullable(),
  erpId: yup.string().nullable(),
  description: yup.string().nullable(),
  contact: yup.object().shape({
    firstName: yup.string().nullable(),
    lastName: yup.string().nullable(),
    email: yup.string().email().nullable(),
    phone: yup.string().nullable(),
    companyName: yup.string().nullable(),
  }),

  billingAddress: yup.object().shape({
    Address: yup.string().nullable(),
    Address2: yup.string().nullable(),
    City: yup.string().nullable(),
    State: yup.string().nullable(),
    Zip: yup.string().nullable(),
    Country: yup.string().nullable(),
    sameAsShipping: yup.boolean().nullable(),
  }),
});

const CustomerForm = () => {
  const navigate = useNavigate();
  const { id: customerId } = useParams();
  const theme = useTheme();
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);
  const { pid: _pid } = useContext(UserContext);
  
  const platforms = useQuery({
    queryID: "platforms",
    request: getPlatforms,
    forcedFilters: {},
  });

  const platformOptions = useMemo(() => {
    const { rows: _platforms = [] } = platforms?.data || {};
    return _platforms.map(({ id, name }) => ({ value: id, label: name }));
  }, [platforms]);

  const profiles = useQuery({
    queryID: "profiles",
    request: getCPQProfileOptions,
    forcedFilters: {},
  });

  const cpqProfileOptions = useMemo(() => {
    const { rows: _profiles = [] } = profiles?.data || {};
    return _profiles.map(({ id, name }) => ({ value: id, label: name }));
  }, [profiles]);  

  const defaultPlatform = useMemo(
    () =>
      platforms.data
        ? _.find(platforms.data, ({ isDefault }) => !!isDefault)
        : {},
    [platforms.data]
  );

  const result = useQuery({
    queryID: `customer|${customerId}`,
    request: getCustomer,
    queryVariables: { id: +customerId },
    forcedFilters: {},
    skip:
      (!customerId && customerId !== 0) ||
      customerId === "create" ||
      platforms?.isLoading,
  });

  const {
    data: currentCustomer,
    isLoading,
    refetchQuery,
    setData,
  } = result || {};

  const { customer, contact, billingAddress, shippingAddresses } =
    currentCustomer || {};
  const { pid, companyName, description, groupId, erpId, cpqProfileOption } = customer || {};

  const { firstName, lastName, email, phone } = contact || {};
  const { Address, Address2, City, State, Zip, Country, sameAsShipping } =
    billingAddress || {};

  const setAddressData = useCallback(
    ({ rows }) => {
      setData({ ...currentCustomer, shippingAddresses: rows });
    },
    [currentCustomer, setData]
  );

  const initialValues = useMemo(
    () => ({
      pid: pid || _pid || defaultPlatform?.id,
      companyName,
      description,
      groupId,
      erpId,
      cpqProfileOption,
      contact: {
        firstName,
        lastName,
        email,
        phone,
      },
      billingAddress: {
        Address,
        Address2,
        City,
        State,
        Zip,
        Country,
        sameAsShipping,
      },
    }),
    [
      pid,
      _pid,
      companyName,
      description,
      groupId,
      erpId,
      cpqProfileOption,
      firstName,
      lastName,
      email,
      phone,
      Address,
      Address2,
      City,
      State,
      Zip,
      Country,
      sameAsShipping,
      defaultPlatform,
    ]
  );

  const onEditAddress = useCallback(async ({ id }) => {
    setAddressId(id);
    setShowCreateAddress(true);
  }, []);

  const onDeleteAddress = useCallback(
    ({ id }) => {
      const _shippingAddresses = [...shippingAddresses];
      _.remove(_shippingAddresses, ({ id: _id }) => _id === id);
      setData({ ...currentCustomer, shippingAddresses: _shippingAddresses });
      deleteAddress({ id });
    },
    [currentCustomer, setData, shippingAddresses]
  );

  const columns = useMemo(
    () => [
      {
        id: "id",
        data: "id",
        header: "id",
        size: { md: -1, sm: -1, xs: -1 },
      },
      {
        id: "Address",
        data: ({ Address }) => <Body ellipsis>{Address || "—"}</Body>,
        header: "Address",
        size: { md: 3, sm: 3, xs: 5 },
      },
      {
        id: "Address2",
        data: ({ Address2 }) => (
          <Body center ellipsis>
            {Address2 || "—"}
          </Body>
        ),
        header: "Address (Line 2)",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "City",
        data: ({ City }) => (
          <Body center ellipsis>
            {City || "—"}
          </Body>
        ),
        header: "City",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "State",
        data: ({ State }) => (
          <Body center ellipsis>
            {State || "—"}
          </Body>
        ),
        header: "State",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "Zip",
        data: ({ Zip }) => (
          <Body center ellipsis>
            {Zip || "—"}
          </Body>
        ),
        header: "Zip",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "Country",
        data: ({ Country }) => (
          <Body center ellipsis>
            {Country || ""}
          </Body>
        ),
        header: "Country",
        size: { md: 2, sm: -1, xs: -1 },
      },

      {
        display: (props) => {
          const uid = props.row.original.id;
          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                color={theme.secondary}
                reverse
                iconName="faPencil"
                onClick={() => onEditAddress({ id: uid })}
              />
              <Button
                size="small"
                color={theme.danger}
                reverse
                iconName="faTrash"
                onClick={() => onDeleteAddress({ id: uid })}
              />
            </ButtonsWrapper>
          );
        },
        header: "Actions",
        sort: false,
        size: { lg: 2, md: 2, sm: 2, xs: 2 },
      },
    ],
    [theme.secondary, theme.danger, onEditAddress, onDeleteAddress]
  );

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      console.log({values});
      try {
        if (customerId === "create") {
          const result = await r.createCustomer({ input: values });
          if (result?.id) {
            navigate(`/Customer/${result?.id}`);
            setData({ ...currentCustomer, customer: result });
            return showSuccessAlert(
              `Successfully created Customer: ${result?.id}`
            );
          } else {
            throw new Error("DB OP FAILED, NO CUSTOMER RETURNED.");
          }
        } else {
          const result = await r.updateCustomer({
            id: customerId,
            input: values,
          });
          if (result) {
            setData({ ...currentCustomer, customer: result });
            showSuccessAlert(`Successfully updated Customer: ${customerId}`);
          } else {
            throw new Error("DB OP FAILED, NO CUSTOMER RETURNED.");
          }
        }
        resetForm();
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed to create customer.");
        resetForm();
      }
    },
    [
      customerId,
      navigate,
      setData,
      currentCustomer,
      showSuccessAlert,
      showFailureAlert,
    ]
  );

  const onDelete = useCallback(async () => {
    await r.deleteCustomer({ id: customerId });
    showSuccessAlert(
      `Succesfully Deleted Customer: ${companyName || customerId}`
    );
    refetchQuery("customers");
    navigate(`/Customers/${pid}`);
  }, [customerId, showSuccessAlert, companyName, refetchQuery, navigate, pid]);

  const stateOptions = useMemo(
    () =>
      states.getStates().map(({ name, abbreviation: a }) => (
        <option value={a} key={a}>
          {name}
        </option>
      )),
    []
  );

  const [showCreateAddress, setShowCreateAddress] = useState(false);
  const [addressId, setAddressId] = useState("create");

  const currentAddress = useMemo(
    () => _.find(shippingAddresses, ({ id }) => id === addressId),
    [addressId, shippingAddresses]
  );

  return (
    <PageWrapper>
      <Modal show={showCreateAddress} setShow={setShowCreateAddress}>
        <Card minWidth="320px">
          <AddressForm
            data={{
              rows: shippingAddresses,
              count: shippingAddresses?.length || 0,
            }}
            count={shippingAddresses?.length || 0}
            addressId={addressId}
            setAddressId={setAddressId}
            customerId={customerId}
            addressType={2}
            currentAddress={currentAddress}
            setShow={setShowCreateAddress}
            setData={setAddressData}
          />
        </Card>
      </Modal>

      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
      >
        {(formProps) => {
          const { values } = formProps;
          return (
            <>
              <HeadingSection
                my="1rem"
                hideCreate
                backButtonLink={() => navigate(`/Customers/${pid || _pid}`)}
                title={`${
                  customerId === "create" ? "Create" : "Edit"
                } Customer`}
              />
              <Form style={{ width: "100%" }}>
                <Field
                  isLoading={isLoading}
                  required
                  labelText="Assign to Platform"
                  name="pid"
                  as="select"
                >
                  <option key="default" value="">
                    Select a Platform
                  </option>
                  {platformOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Field>

                <Field
                  isLoading={isLoading}
                  required
                  labelText="Country Profile"
                  name="cpqProfileOption"
                  as="select"
                >
                  <option key="default" value="">
                    Select a Profile
                  </option>
                  {cpqProfileOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Field>

                <Field
                  isLoading={isLoading}
                  labelText="Name"
                  name="companyName"
                />
                <Field isLoading={isLoading} labelText="ERP ID" name="erpId" />

                <Field
                  isLoading={isLoading}
                  labelText="Description"
                  as="textarea"
                  name="description"
                  max={500}
                  description="500 characters maximum."
                />

                <Flex
                  width={1}
                  flexDirection={["column", "column", "row"]}
                  style={{ gap: "1rem" }}
                >
                  <Card
                    style={{ display: "block" }}
                    alignItems="flex-start"
                    width={1}
                  >
                    <Heading mt="0">Contact Information</Heading>
                    <Field
                      isLoading={isLoading}
                      name="contact.firstName"
                      labelText="First Name"
                    />
                    <Field
                      isLoading={isLoading}
                      name="contact.lastName"
                      labelText="Last Name"
                    />
                    <Field
                      isLoading={isLoading}
                      name="contact.email"
                      labelText="Email"
                    />
                    <Field
                      isLoading={isLoading}
                      name="contact.phone"
                      labelText="Phone"
                      phone
                    />
                  </Card>

                  <Card
                    style={{ display: "block" }}
                    width={1}
                    flexDirection="column"
                    alignItems="flex-end"
                  >
                    <Heading mt="0">Billing Address</Heading>
                    <Box mt="0.55rem" mb="1rem">
                      <Checkbox
                        isLoading={isLoading}
                        labelText="Shipping Address is same as Billing?"
                        name="billingAddress.sameAsShipping"
                      />
                    </Box>
                    <Field
                      isLoading={isLoading}
                      name="billingAddress.Address"
                      labelText="Address Line 1"
                    />
                    <Field
                      isLoading={isLoading}
                      name="billingAddress.Address2"
                      labelText="Address Line 2"
                    />
                    <Field
                      isLoading={isLoading}
                      name="billingAddress.City"
                      labelText="City"
                    />
                    <Field
                      isLoading={isLoading}
                      as="select"
                      name="billingAddress.State"
                      labelText="State"
                    >
                      <option value="" key="default">
                        Select a State
                      </option>
                      {stateOptions}
                    </Field>
                    <Field
                      isLoading={isLoading}
                      name="billingAddress.Zip"
                      labelText="Zip"
                    />
                    <Field
                      isLoading={isLoading}
                      name="billingAddress.Country"
                      labelText="Country"
                    />
                  </Card>
                </Flex>

                <FormButtonCluster
                  onDelete={onDelete}
                  onCancel={() => navigate(`/Customers/${pid}`)}
                  hideDelete
                  recordAction={customerId === "create" ? "create" : "edit"}
                  deleteMessage={`Are you sure you want to delete ${values.name}?`}
                />

                {!values.billingAddress?.sameAsShipping &&
                  customerId !== "create" && (
                    <Table
                      createButtonText="Create Address"
                      createLink={() => {
                        setShowCreateAddress(true);
                        setAddressId("create");
                      }}
                      title="Assign Shipping Addresses"
                      subtitle="Use this table control to assign or unassign Shipping Addresses from this customer. These will be available to auto-fill from the customer page of a quote with this customer selected."
                      data={shippingAddresses}
                      hideToggle
                      columns={columns}
                      hideBack
                      hideSearch
                      hideProperties
                      backButtonLink="/Administration"
                      isLoading={isLoading}
                      totalCount={shippingAddresses?.length || 0}
                    />
                  )}
              </Form>
            </>
          );
        }}
      </Formik>
    </PageWrapper>
  );
};

export default CustomerForm;
